import { EventEmitter } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import type { ComponentViewModelInterface } from '@nts/std/interfaces';
import { UUIDHelper } from '@nts/std/utility';
import { PropertyViewModelPropertyChangedEventArgs } from './property-view-property-changed-event-args';

export class ComponentViewModelUtility {
  static getRootViewModel<TViewModel>(viewModel: { parent?: any; }): TViewModel {
    let current: any = viewModel;

    while (current?.parent != null) {
      current = current.parent;
    }

    return current as TViewModel;
  }

  /**
   * Retrieves a component by its ID from the given map of components. If the component does not exist, it will be created using the provided factory function or the map's factory method.
   *
   * @param components The map of components to retrieve the component from.
   * @param componentId The ID of the component to retrieve.
   * @param factory The factory function to create a new component if it doesn't exist.
   * @returns The component view model corresponding to the given ID.
   */
  static getComponentById<TComponent extends ComponentViewModelInterface>(
    components: Map<string, TComponent>,
    componentId: string,
    factory?: () => TComponent
  ): TComponent {
    let componentVm = components?.get(componentId);

    if (componentVm == null) {
      componentVm = factory?.() ?? (
        components instanceof ComponentViewModelMap
          ? components.createComponent()
          : new ComponentViewModel() as unknown as TComponent
      );
      components?.set(componentId, componentVm);
    }

    return componentVm;
  }

  /**
   * Sets the visibility of a component by its ID.
   *
   * @param components The map of components to retrieve the component from.
   * @param componentId The ID of the component to retrieve.
   * @param value The visibility value to set on the component.
   * @returns The component view model with the updated visibility.
   */
  static setIsVisible<TComponent extends ComponentViewModelInterface>(
    components: Map<string, TComponent>,
    componentId: string,
    value: boolean
  ): TComponent {
    const componentVm = this.getComponentById(components, componentId);
    componentVm.isVisible = value;
    return componentVm;
  }
}

export class ComponentViewModelMap<TComponent extends ComponentViewModelInterface> extends Map<string, TComponent> {
  constructor(private readonly factory: () => TComponent) {
    super();
  }

  createComponent(): TComponent {
    return this.factory();
  }
}

export class ComponentViewModel implements ComponentViewModelInterface {
  propertyChanged: EventEmitter<PropertyViewModelPropertyChangedEventArgs> = new EventEmitter();
  readonly uniqueId = UUIDHelper.generateUUID();

  protected internalIsVisible = true;

  get isVisible() {
    return this.internalIsVisible;
  }

  set isVisible(value: boolean) {
    if (this.internalIsVisible !== value) {
      this.internalIsVisible = value;
      this.onPropertyChanged('isVisible');
      this.isVisible$.next(value);
    }
  }

  isVisible$ = new BehaviorSubject<boolean>(this.internalIsVisible);

  onPropertyChanged(propertyName: string = null) {
    const args = new PropertyViewModelPropertyChangedEventArgs();
    args.propertyName = propertyName;
    this.propertyChanged.emit(args);
  }
}
